import {Form, Modal, Row, Col, DatePicker, TimePicker, Select, Switch, Input, Alert} from "antd";
import React, {useMemo, useState, useEffect} from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {debounce} from "lodash";
import * as Yup from "yup";
import localeTr from "antd/es/date-picker/locale/tr_TR";
import localeEn from "antd/es/date-picker/locale/en_US";
import locale from "antd/es/date-picker/locale/tr_TR";
import {CompanyFilter} from "../../../_defaultComponents/FilterComponents";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {SHIFT_WORK_TYPES, isPoolWorkType} from "../../../../constants/shiftWorkTypes";
import {ErrorMessage, SelectField} from "./ShiftEntryModalStyles";
import ShiftEntryDetailModal from "./ShiftEntryDetailModal";
import {CourierDetailBox} from "./CourierDetailBox/CourierDetailBox";

const DEBOUNCE_TIMEOUT = 800;

// Boş metin input'unu NaN yerine undefined'a çevirir; böylece alan gizliyken (geçmiş
// olmayan vardiya) "sayı değil" hatası üretmez, geçmiş dedike vardiyada ise .required() çalışır.
const optionalNumber = Yup.number().transform((value, originalValue) =>
    String(originalValue).trim() === "" ? undefined : value
);

// Seçilen tarihe, seçilen saatin saat/dakikasını uygular.
const applyTimeToDate = (date, time) => {
    const t = moment(time);
    return moment(date)
        .hour(t.hour())
        .minute(t.minute())
        .second(0)
        .millisecond(0);
};

// Tarih + başlangıç/bitiş saatinden vardiya bitiş anını kurar; gece aşan vardiyada (bitiş,
// başlangıçtan önce) bitiş ertesi güne taşınır. Tarih ya da saatlerden biri eksikse null.
const getShiftEndMoment = (date, startTime, endTime) => {
    if (!date || !startTime || !endTime) return null;
    const startMoment = applyTimeToDate(date, startTime);
    const endMoment = applyTimeToDate(date, endTime);
    if (endMoment.isBefore(startMoment)) endMoment.add(1, "day");
    return endMoment;
};

/**
 * Alt firma tipine göre üç varyant:
 *  - Havuz: Havuz · Tarih · Kurye · Başlangıç · Bitiş · Joker · Joker mesafesi
 *  - Sabit Entegre: Firma · Şube · Tarih · Kurye · Başlangıç · Bitiş · Joker · Joker mesafesi
 *  - Sabit: Firma · Şube · Tarih · Kurye · Başlangıç · Bitiş
 *
 * Entegre iki tipte (havuz + sabit entegre) vardiya saatlerinin dakikası yalnız 00 veya 30
 * olabilir; sabit tipte tüm dakikalar seçilebilir. Entegre kayıt havuz slotlarına yazıldığı
 * için seçilen saat, kaydederken genel vardiya saat listesindeki id'siyle eşleştirilir; havuzun
 * vardiya saatleri seçimi kısıtlamaz.
 */
const ShiftEntryModal = ({visible, onCancel, setIsModalOpen, setReloadData, workType}) => {
    const {t, i18n} = useTranslation();
    const stores = useStore();
    const isIntegrated = isPoolWorkType(workType);
    const isPool = workType === SHIFT_WORK_TYPES.POOL;

    // Sabit (düz dedike) vardiyada, seçilen tarih + başlangıç/bitiş saatiyle kurulan vardiya
    // bitişi şu andan önceyse kayıt geçmiş vardiya sayılır: puantaj raporu ucuna
    // (createShiftReport) gider, çalışma süresi + paket sayısı zorunlu olur. Havuz ve sabit
    // entegre tiplerinde geçmiş vardiya akışı yok.
    const isPastDedicatedShift = ({date, shiftStartTime, shiftEndTime}) => {
        if (isIntegrated) return false;
        const endMoment = getShiftEndMoment(date, shiftStartTime, shiftEndTime);
        return !!endMoment && endMoment.isBefore(moment());
    };

    const [searchText, setSearchText] = useState("");
    const [courierId, setCourierId] = useState("");
    const [isJoker, setIsJoker] = useState(false);
    const [jokerDistance, setJokerDistance] = useState(1000);
    const [submitError, setSubmitError] = useState(null);

    const formValidation = Yup.object().shape({
        ...(isPool
            ? {pool: Yup.string().required(t("validation.required", {ns: "common"}))}
            : {
                  company: Yup.string().required(t("validation.required", {ns: "common"})),
                  subCompany: Yup.string().required(t("validation.required", {ns: "common"}))
              }),
        date: Yup.string().required(t("validation.required", {ns: "common"})),
        courier: Yup.string().required(t("validation.required", {ns: "common"})),
        shiftStartTime: Yup.string().required(t("validation.required", {ns: "common"})),
        shiftEndTime: Yup.string().required(t("validation.required", {ns: "common"})),
        // Boş input'u NaN'a çevirip "sayı değil" hatası vermesin diye undefined'a düşürülüyor;
        // yalnız vardiya bitişi geçmişte kalan dedike vardiyada zorunlu.
        workTimeMinute: optionalNumber.when(["date", "shiftStartTime", "shiftEndTime"], {
            is: (date, shiftStartTime, shiftEndTime) =>
                isPastDedicatedShift({date, shiftStartTime, shiftEndTime}),
            then: optionalNumber.required(t("validation.required", {ns: "common"})),
            otherwise: optionalNumber
        }),
        packageCount: optionalNumber.when(["date", "shiftStartTime", "shiftEndTime"], {
            is: (date, shiftStartTime, shiftEndTime) =>
                isPastDedicatedShift({date, shiftStartTime, shiftEndTime}),
            then: optionalNumber.required(t("validation.required", {ns: "common"})).max(99),
            otherwise: optionalNumber
        })
    });

    const [, subCompanies] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: stores.meStore.map.filters.company,
            limit: 1000
        }),
        condition: !isPool && !!stores.meStore.map.filters.company,
        dependencies: [stores.meStore.map.filters.company, visible],
        initialCall: false
    });

    /* Havuz vardiyası kaydederken poolId gerekiyor: havuz tipinde doğrudan seçiliyor,
       sabit entegrede seçilen şubenin bağlı olduğu havuz üzerinden çözülüyor. */
    const [, userPools] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolStore.getUserPools.bind(null, {}),
        dependencies: [visible],
        condition: visible && isIntegrated,
        initialCall: false
    });

    const [, poolList] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolStore.getPoolList.bind(null, {limit: 1000, page: 1}),
        dependencies: [visible],
        condition: visible && workType === SHIFT_WORK_TYPES.DEDICATED_INTEGRATION,
        initialCall: false
    });

    /* Havuz ucu saat değil slot id'si beklediği için seçilen saatler genel vardiya saat
       listesinden id'ye çevrilir. */
    const [, shiftHours] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolShiftStore.getShiftHours.bind(null, {}),
        dependencies: [visible],
        condition: visible && isIntegrated,
        initialCall: false
    });

    const [, courierList] = useFetch({
        fetchFn: stores.courierStore.getCourierList.bind(null, {
            limit: 10,
            skip: 0,
            statuses: [],
            types: [],
            searchText
        }),
        dependencies: [searchText, visible],
        condition: visible && !isPool && !stores.meStore.checkRole("isCompanyUser"),
        initialCall: false
    });

    const [, courierData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierStore.getCourier.bind(null, {courierId}),
        dependencies: [courierId],
        condition: !stores.meStore.checkRole("isCompanyUser") && courierId
    });

    const formik = useFormik({
        initialValues: {
            pool: "",
            company: "",
            subCompany: "",
            subCompanyId: "",
            date: "",
            courier: "",
            shiftStartTime: "",
            shiftEndTime: "",
            workTimeMinute: "",
            packageCount: ""
        },
        validationSchema: formValidation,
        enableReinitialize: true,
        onSubmit: async values => {
            setSubmitError(null);
            if (isPastDedicatedShift(values)) {
                await savePastShift();
            } else if (isIntegrated) {
                await savePoolShift();
            } else {
                await saveDedicatedShift();
            }
        }
    });

    const {values, setFieldValue, handleSubmit, errors, touched} = formik;

    const isPastShiftEntry = isPastDedicatedShift(values);

    // Sabit entegre havuzları tek şubeye bağlı; seçilen şubeyi içeren havuz aranır.
    const selectedPoolId = useMemo(() => {
        if (isPool) return values.pool;
        if (workType !== SHIFT_WORK_TYPES.DEDICATED_INTEGRATION || !values.subCompanyId) return "";
        const matchedPool = (poolList?.data || []).find(pool =>
            (pool.subCompanies || []).some(
                subCompany => String(subCompany._id) === String(values.subCompanyId)
            )
        );
        return matchedPool?._id || "";
    }, [isPool, workType, values.pool, values.subCompanyId, poolList]);

    /* Havuz tipinde kurye listesi, seçilen havuza vardiya yazılabilecek (kullanıcının yetki
       kapsamındaki) aktif kuryelerdir; diğer tiplerde genel kurye aramasından gelir. İki tipte
       de arama sunucuda yapılır ve ilk 10 kurye gelir. */
    const [, poolCouriers] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.poolShiftStore.getPoolCouriers.bind(null, {
            poolId: selectedPoolId,
            searchText
        }),
        dependencies: [selectedPoolId, searchText],
        condition: visible && isPool && !!selectedPoolId,
        initialCall: false
    });

    const courierOptions = isPool
        ? (selectedPoolId && poolCouriers?.data) || []
        : courierList?.rows || [];

    useEffect(() => {
        if (visible) {
            formik.resetForm();
            setCourierId("");
            setIsJoker(false);
            setJokerDistance(1000);
            setSubmitError(null);
        }
    }, [visible, workType]);

    const formatTime = time => (time ? moment(time).format("HH:mm") : "");

    const findHourIdByTime = (time, timeKey) =>
        (shiftHours?.data || []).find(hour => hour[timeKey] === formatTime(time))?._id;

    const savePoolShift = async () => {
        if (!selectedPoolId) {
            setSubmitError("Seçilen şube için bağlı bir havuz bulunamadı.");
            return;
        }
        const startHourId = findHourIdByTime(values.shiftStartTime, "startTime");
        const endHourId = findHourIdByTime(values.shiftEndTime, "endTime");
        const result = await stores.poolShiftStore.bookMultipleSlots({
            date: moment(values.date).format("YYYY-MM-DD"),
            poolId: selectedPoolId,
            courierId,
            startHourId,
            endHourId,
            fromShiftManagement: true,
            isJoker,
            jokerDistance
        });
        // Hata modalı store'da açılır; form kaybolmasın diye modal açık bırakılır.
        if (!result) return;
        // Kuryenin başka havuzda vardiyası varsa uç 200 döner ama vardiyayı oluşturmaz.
        if (result.fullfilled === false) {
            const conflictPoolNames = (result.error?.pools || [])
                .map(pool => pool?.name)
                .filter(Boolean)
                .join(", ");
            setSubmitError(
                conflictPoolNames ? `${result.message} (${conflictPoolNames})` : result.message
            );
            return;
        }
        setReloadData();
        setIsModalOpen(false);
    };

    const saveDedicatedShift = async () => {
        const result = await stores.shiftStore.createManuelShift({
            shiftDate: moment(values.date).format("YYYY-MM-DD"),
            companyId: stores.appStore.companyListFilter
                ?.filter(c => c._id === values.company)
                ?.map(c => c._id)[0],
            subCompanyId: values.subCompanyId,
            courierId,
            shiftStartTime: formatTime(values.shiftStartTime),
            shiftEndTime: formatTime(values.shiftEndTime)
        });
        if (!result) return;
        setReloadData();
        setIsModalOpen(false);
    };

    /* Geçmiş tarihli dedike vardiya, canlı vardiya değil geçmiş puantaj kaydı olduğu için
       normal vardiya ucu yerine puantaj raporu ucuna yazılır. */
    const savePastShift = async () => {
        const result = await stores.shiftStore.createShiftReport({
            shiftDate: moment(values.date).format("YYYY-MM-DD"),
            companyId: stores.appStore.companyListFilter
                ?.filter(c => c._id === values.company)
                ?.map(c => c._id)[0],
            subCompanyId: values.subCompanyId,
            courierId,
            shiftStartTime: formatTime(values.shiftStartTime),
            shiftEndTime: formatTime(values.shiftEndTime),
            workTimeMinute: Number(values.workTimeMinute),
            packageCount: Number(values.packageCount)
        });
        if (!result) return;
        setReloadData();
        setIsModalOpen(false);
    };

    // Sabit (dedike) vardiyada geçmiş tarih seçilebilir; havuz ve sabit entegrede yalnız
    // bugün ve sonrası açık.
    const getDisabledDate = current => {
        if (!current) return false;
        if (!isIntegrated) return false;
        return current < moment(moment().format("DD.MM.YYYY"), "DD.MM.YYYY");
    };

    const isToday = () =>
        moment().format("DD.MM.YYYY") === moment(values.date).format("DD.MM.YYYY");

    const disabledHours = () => {
        // Dedike tipte bugün içinde bitmiş bir vardiya geçmiş vardiya olarak girilebildiği için
        // geçmiş saatler açık; entegre tiplerde geçmiş slot bookinglenemez.
        if (!isIntegrated || !isToday()) return [];
        const currentHour = moment().hour();
        return Array.from({length: currentHour}, (_, index) => index);
    };

    const disabledMinutes = selectedHour => {
        const minutes = [];
        // Entegre firmalarda (havuz + sabit entegre) vardiya saatleri yarım saatlik dilimlere
        // oturmak zorunda ve bugünün geçmiş dakikaları kapalı; dedike firmalarda tüm dakikalar
        // seçilebilir (geçmiş vardiya girişi için gerekli).
        if (isIntegrated) {
            for (let minute = 0; minute < 60; minute++) {
                if (minute !== 0 && minute !== 30) minutes.push(minute);
            }
            if (isToday() && selectedHour === moment().hour()) {
                for (let minute = 0; minute <= moment().minute(); minute++) {
                    if (!minutes.includes(minute)) minutes.push(minute);
                }
            }
        }
        return minutes;
    };

    const timeDiffMinutes = () => {
        const start = formatTime(values.shiftStartTime);
        const end = formatTime(values.shiftEndTime);
        if (!start || !end) return 0;

        const startMoment = moment(start, "HH:mm");
        const endMoment = moment(end, "HH:mm");
        if (endMoment.isBefore(startMoment)) endMoment.add(1, "day");
        return endMoment.diff(startMoment, "minutes");
    };

    const onSearchUser = useMemo(() => debounce(value => setSearchText(value), DEBOUNCE_TIMEOUT), [
        DEBOUNCE_TIMEOUT
    ]);

    const resetCourier = () => {
        setFieldValue("courier", "");
        setCourierId("");
    };

    const renderShiftTimeField = field => {
        const label =
            field === "shiftStartTime"
                ? t("shiftScore.shiftStartTime", {ns: "shiftScore"})
                : t("shiftScore.shiftEndTime", {ns: "shiftScore"});

        return (
            <Form.Item>
                <h4>{label}</h4>
                <TimePicker
                    format={"HH:mm"}
                    allowClear={false}
                    minuteStep={isIntegrated ? 30 : 1}
                    value={values[field] || null}
                    onSelect={date => setFieldValue(field, date)}
                    locale={i18n?.resolvedLanguage === "en" ? localeEn : localeTr}
                    disabledHours={field === "shiftStartTime" ? disabledHours : undefined}
                    disabledMinutes={disabledMinutes}
                />
                {errors[field] && touched[field] ? (
                    <ErrorMessage>{errors[field]}</ErrorMessage>
                ) : null}
            </Form.Item>
        );
    };

    // Geçmiş dönemli vardiyada, kaydetmeden önce ayrı bir onay modalı çıkar.
    const handleOk = () => {
        if (!isPastShiftEntry) {
            handleSubmit();
            return;
        }
        Modal.confirm({
            title: t("shiftScore.pastShiftConfirm", {
                ns: "shiftScore",
                courier: courierData?.data?.fullname || "",
                date: values.date ? moment(values.date).format("DD.MM.YYYY") : ""
            }),
            okText: t("modal.yesButton", {ns: "common"}),
            cancelText: t("modal.noButton", {ns: "common"}),
            onOk: handleSubmit
        });
    };

    return (
        <Modal
            centered
            width={920}
            visible={visible}
            onOk={handleOk}
            onCancel={onCancel}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form>
                {isPastShiftEntry && (
                    // Geçmiş dönemli vardiya kaydı geri alınamadığı için kaydetmeden önce uyarılır.
                    <Alert
                        type="warning"
                        showIcon
                        style={{marginBottom: 16}}
                        message={t("shiftScore.pastShiftWarning", {
                            ns: "shiftScore",
                            courier: courierData?.data?.fullname || "",
                            date: values.date ? moment(values.date).format("DD.MM.YYYY") : ""
                        })}
                    />
                )}
                <Row>
                    <Col span={11}>
                        {isPool ? (
                            <Form.Item>
                                <h4>{t("fields.pool", {ns: "common"})}</h4>
                                <Select
                                    allowClear
                                    showSearch
                                    style={{width: "100%"}}
                                    value={values.pool || undefined}
                                    onChange={value => {
                                        setFieldValue("pool", value || "");
                                        setFieldValue("date", "");
                                        resetCourier();
                                    }}
                                    options={(userPools?.data || []).map(pool => ({
                                        label: pool.name,
                                        value: pool._id
                                    }))}
                                    filterOption={(input, option) =>
                                        String(option?.label || "")
                                            .toLocaleLowerCase("tr-TR")
                                            .includes(input.toLocaleLowerCase("tr-TR"))
                                    }
                                />
                                {errors.pool && touched.pool ? (
                                    <ErrorMessage>{errors.pool}</ErrorMessage>
                                ) : null}
                            </Form.Item>
                        ) : (
                            <>
                                <Form.Item>
                                    <h4>{t("fields.company", {ns: "common"})}</h4>
                                    <CompanyFilter
                                        hideTitle
                                        value={values.company}
                                        onChange={value => {
                                            setFieldValue("company", value);
                                            setFieldValue("subCompany", "");
                                            setFieldValue("subCompanyId", "");
                                            resetCourier();
                                            stores.meStore.map.filters.company = value;
                                        }}
                                    />
                                    {errors.company && touched.company ? (
                                        <ErrorMessage>{errors.company}</ErrorMessage>
                                    ) : null}
                                </Form.Item>
                                <Form.Item>
                                    <h4>{t("fields.subcompany", {ns: "common"})}</h4>
                                    <SelectField
                                        allowClear
                                        showSearch
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        value={values.subCompany}
                                        onChange={(value, key) => {
                                            setFieldValue("subCompany", value);
                                            setFieldValue("subCompanyId", key?.key);
                                            stores.meStore.map.filters.subCompany = value;
                                        }}
                                    >
                                        {subCompanies.rows &&
                                            subCompanies?.rows.map(subcompany => (
                                                <SelectField.Option
                                                    key={subcompany._id}
                                                    value={subcompany.name}
                                                    data-testid="subcompanySelect_option"
                                                >
                                                    {subcompany.name}
                                                </SelectField.Option>
                                            ))}
                                    </SelectField>
                                    {errors.subCompany && touched.subCompany ? (
                                        <ErrorMessage>{errors.subCompany}</ErrorMessage>
                                    ) : null}
                                </Form.Item>
                            </>
                        )}

                        <Form.Item>
                            <h4>{t("fields.date", {ns: "common"})}</h4>
                            <DatePicker
                                locale={locale}
                                value={values.date || null}
                                format={"DD.MM.YYYY"}
                                onSelect={date => setFieldValue("date", date)}
                                disabledDate={getDisabledDate}
                            />
                            {errors.date && touched.date ? (
                                <ErrorMessage>{errors.date}</ErrorMessage>
                            ) : null}
                        </Form.Item>

                        <Form.Item>
                            <h4>{t("fields.courier", {ns: "common"})}</h4>
                            <SelectField
                                allowClear
                                showSearch
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                value={values.courier}
                                onSearch={onSearchUser}
                                onChange={(value, option) => {
                                    setCourierId(option?.key);
                                    setFieldValue("courier", value);
                                }}
                                filterOption={false}
                            >
                                {courierOptions.map(courier => (
                                    <SelectField.Option
                                        key={courier._id}
                                        value={[courier.fullname, courier.tckn, courier.cellPhone]
                                            .filter(Boolean)
                                            .join(" ")}
                                        data-testid="courierSelect_option"
                                    >
                                        <CourierDetailBox
                                            courierName={courier.fullname}
                                            courierTckn={courier.tckn}
                                            courierCellPhone={courier.cellPhone}
                                        />
                                    </SelectField.Option>
                                ))}
                            </SelectField>
                            {errors.courier && touched.courier ? (
                                <ErrorMessage>{errors.courier}</ErrorMessage>
                            ) : null}
                        </Form.Item>

                        {renderShiftTimeField("shiftStartTime")}
                        {renderShiftTimeField("shiftEndTime")}

                        {isPastShiftEntry && (
                            <>
                                <Form.Item>
                                    <h4>{t("shiftScore.workTime", {ns: "shiftScore"})}</h4>
                                    <Input
                                        type="number"
                                        min={0}
                                        placeholder={
                                            t("shiftScore.workTime", {ns: "shiftScore"}) + " (dk)"
                                        }
                                        value={values.workTimeMinute}
                                        onChange={e =>
                                            setFieldValue("workTimeMinute", e.target.value)
                                        }
                                    />
                                    {errors.workTimeMinute && touched.workTimeMinute ? (
                                        <ErrorMessage>{errors.workTimeMinute}</ErrorMessage>
                                    ) : null}
                                </Form.Item>
                                <Form.Item>
                                    <h4>{t("shiftScore.packageCount", {ns: "shiftScore"})}</h4>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={99}
                                        placeholder={t("shiftScore.packageCount", {ns: "shiftScore"})}
                                        value={values.packageCount}
                                        onChange={e =>
                                            e.target.value <= 99 &&
                                            setFieldValue("packageCount", e.target.value)
                                        }
                                    />
                                    {errors.packageCount && touched.packageCount ? (
                                        <ErrorMessage>{errors.packageCount}</ErrorMessage>
                                    ) : null}
                                </Form.Item>
                            </>
                        )}

                        {isIntegrated && (
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item>
                                        <h4>{t("shiftScore.jokerCourier", {ns: "shiftScore"})}</h4>
                                        <Switch checked={isJoker} onChange={setIsJoker} />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item>
                                        <h4>
                                            {t("shiftScore.jokerCourierDistance", {
                                                ns: "shiftScore"
                                            })}
                                        </h4>
                                        <Input
                                            value={jokerDistance}
                                            onChange={e => setJokerDistance(e.target.value)}
                                            disabled={!isJoker}
                                            type="number"
                                            min={0}
                                            style={{width: "100%"}}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
                    </Col>
                    <Col offset={1} span={12}>
                        <ShiftEntryDetailModal
                            reportDate={values.date && moment(values.date).format("DD.MM.YYYY")}
                            fullname={courierData?.data?.fullname}
                            tckn={courierData?.data?.tckn}
                            cellPhone={courierData?.data?.cellPhone}
                            companyName={
                                isPool
                                    ? (userPools?.data || []).find(
                                          pool => pool._id === values.pool
                                      )?.name
                                    : stores.appStore.companyListFilter
                                          ?.filter(c => c._id === values.company)
                                          ?.map(c => c.companyName)
                            }
                            subCompanyName={values.subCompany}
                            shiftStartTime={formatTime(values.shiftStartTime)}
                            shiftEndTime={formatTime(values.shiftEndTime)}
                            workTimeMinute={
                                isPastShiftEntry
                                    ? Number(values.workTimeMinute)
                                    : timeDiffMinutes()
                            }
                            packageCount={isPastShiftEntry ? values.packageCount : undefined}
                        />
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ShiftEntryModal;
